<template>
  <div class="pa-4">
    <div class="mb-3 d-flex align-center">
      <DateTimeFilter
        v-model="dateRange"
        :time-zone="facilityTimeZone"
        :ranges="['1m']"
        default="1m"
        max-range="1m"
        custom
        inverted
      />
    </div>

    <v-alert type="info" text dense class="mb-3">
      If you have submitted a survey previously, it may take some time for it to
      appear in the history.
    </v-alert>

    <v-skeleton-loader v-if="isLoading" type="list-item-avatar-two-line" />
    <template v-else-if="surveyList.length > 0">
      <p class="caption grey--text mb-3">
        {{ surveyList.length }} survey submission{{
          surveyList.length !== 1 ? "s" : ""
        }}
        for this incident
      </p>
      <v-expansion-panels v-model="openPanels" multiple accordion>
        <v-expansion-panel v-for="(entry, index) in surveyList" :key="entry.id">
          <v-expansion-panel-header>
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <span class="font-weight-medium body-2">
                Submission #{{ surveyList.length - index }}
              </span>
              <v-chip
                v-if="index === 0"
                x-small
                color="blue lighten-4"
                text-color="blue darken-3"
              >
                Latest
              </v-chip>
              <span class="caption grey--text">
                Submitted by {{ entry.submittedBy }} · {{ entry.insertedAt }}
              </span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row dense>
              <v-col
                v-for="field in fieldsFor(entry)"
                :key="field.key"
                :cols="field.fullWidth ? 12 : 6"
                class="mb-2"
              >
                <div class="caption grey--text text-uppercase mb-1">
                  {{ field.label }}
                </div>
                <div
                  v-if="field.value"
                  :class="['body-2', valueColorClass(field.key, field.value)]"
                  :style="field.fullWidth ? detailStyle : ''"
                >
                  {{ field.displayValue }}
                </div>
                <div v-else class="caption grey--text font-italic">
                  — Not provided —
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
    <v-alert v-else type="info" outlined>
      No survey submissions found for the selected date range.
    </v-alert>
  </div>
</template>

<script>
import { ref } from "vue";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import useIncident from "./useIncident";
import useFacility from "../authentication/useFacility";
import { titleCase } from "@tod-ui/helpers/strings";
import { AUDIT_LOGS } from "../audit_log/graphql";

const FIELDS = {
  visualInspection: {
    label: "Visual Inspection",
    fullWidth: false,
    applyTitleCase: true,
  },
  visualInspectionDescr: {
    label: "Visual Inspection Description",
    fullWidth: true,
  },
  breakInDetected: {
    label: "Break-In Detected",
    fullWidth: false,
    applyTitleCase: true,
  },
  gateAccessCode: {
    label: "Gate Access Code Used",
    fullWidth: false,
    applyTitleCase: true,
  },
  damagedLock: {
    label: "Damage to Lock",
    fullWidth: false,
    applyTitleCase: true,
  },
  damagedDoor: {
    label: "Damage to Door",
    fullWidth: false,
    applyTitleCase: true,
  },
  footageInspection: {
    label: "Video Footage Inspection",
    fullWidth: false,
    applyTitleCase: true,
  },
  footageInspectionDescr: {
    label: "Video Footage Description",
    fullWidth: true,
  },
};

const YES_NO_FIELDS = new Set([
  "visualInspection",
  "breakInDetected",
  "gateAccessCode",
  "damagedLock",
  "damagedDoor",
  "footageInspection",
]);

const detailStyle =
  "background:#f9f9f9;border:1px solid #e0e0e0;border-radius:4px;padding:8px 12px;line-height:1.5";

export default {
  name: "CardSurveyHistory",
  components: { DateTimeFilter },
  setup() {
    const { incidentId } = useIncident();
    const {
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
    } = useFacility();
    const openPanels = ref([0]);
    return {
      incidentId,
      currentFacilityId,
      facilityTimeZone,
      facilityFormatDateTime4Humans,
      openPanels,
      detailStyle,
    };
  },
  data() {
    return {
      rawHistory: [],
      dateRange: { after: undefined, before: undefined },
    };
  },
  computed: {
    isLoading() {
      return this.$apollo.queries.rawHistory.loading;
    },
    surveyList() {
      return this.rawHistory.map((entry) => {
        const survey = this.parseSurvey(entry);
        return {
          id: entry.id,
          ...survey,
          submittedBy: survey?.user || entry.actorName || "System",
          insertedAt: this.facilityFormatDateTime4Humans(entry.insertedAt),
        };
      });
    },
  },
  watch: {
    surveyList: {
      immediate: true,
      handler(list) {
        this.$emit("count-updated", list.length);
      },
    },
  },
  apollo: {
    rawHistory: {
      query: AUDIT_LOGS,
      variables() {
        return {
          facilityId: this.currentFacilityId,
          entityId: this.incidentId,
          dateTimeFilter: this.dateRange,
        };
      },
      skip() {
        return (
          !this.incidentId || !this.currentFacilityId || !this.dateRange?.after
        );
      },
      update(data) {
        return (data?.auditLogs ?? []).filter(
          (log) => log.changes?.survey != null
        );
      },
      error(e) {
        console.error("Failed to load survey history:", e);
      },
    },
  },
  methods: {
    parseSurvey(entry) {
      try {
        const newSurvey = entry.changes?.survey?.new;
        if (typeof newSurvey === "string") return JSON.parse(newSurvey);
        if (typeof newSurvey === "object" && newSurvey !== null)
          return newSurvey;
        const rawSurvey = entry.data?.survey;
        if (typeof rawSurvey === "string") return JSON.parse(rawSurvey);
        return rawSurvey ?? {};
      } catch {
        return {};
      }
    },
    fieldsFor(entry) {
      return Object.entries(FIELDS).map(([key, config]) => {
        const raw = entry[key] ?? null;
        return {
          key,
          label: config.label,
          fullWidth: config.fullWidth,
          value: raw || null,
          displayValue: raw
            ? config.applyTitleCase
              ? titleCase(raw)
              : raw
            : null,
        };
      });
    },
    valueColorClass(key, value) {
      if (!YES_NO_FIELDS.has(key)) return "";
      const v = value?.toLowerCase();
      if (v === "yes") return "success--text";
      if (v === "no") return "error--text";
      return "grey--text";
    },
  },
};
</script>
